import React, { useState } from 'react';
import { 
  Card, 
  Steps, 
  Button, 
  Form, 
  Input, 
  Select, 
  Space, 
  message, 
  Spin, 
  Typography, 
  Divider,
  Alert,
  Modal,
  Checkbox,
  Tag,
  Collapse,
  List,
  Badge
} from 'antd';
import { 
  RobotOutlined, 
  CheckCircleOutlined, 
  LoadingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  InteractionOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  ForkOutlined
} from '@ant-design/icons';
import apiClient, { API_BASE } from '../api';
import FunctionForm from './FunctionForm';
import KnowledgeBaseManager from './KnowledgeBaseManager';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const BotManager = ({ templates, functions, loading, onUpdate, account }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [workflowState, setWorkflowState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reasoning, setReasoning] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);
  const [approvalQuestion, setApprovalQuestion] = useState('');
  const [customFunctions, setCustomFunctions] = useState([]);
  const [customFunctionsCreated, setCustomFunctionsCreated] = useState([]);
  const [customFunctionErrors, setCustomFunctionErrors] = useState([]);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [showCustomFunctionForm, setShowCustomFunctionForm] = useState(false);
  const [editingCustomFunction, setEditingCustomFunction] = useState(null);
  const [createdBotId, setCreatedBotId] = useState(null);

  const steps = [
    {
      title: 'Bot Purpose',
      icon: <RobotOutlined />,
      description: 'Define your vision'
    },
    {
      title: 'Core Settings',
      icon: <SettingOutlined />,
      description: 'Account & Identity'
    },
    {
      title: 'Capabilities',
      icon: <ThunderboltOutlined />,
      description: 'Functions & Skills'
    },
    {
      title: 'Collaboration',
      icon: <InteractionOutlined />,
      description: 'Handover & Routing'
    },
    {
      title: 'Review & Deploy',
      icon: <CheckCircleOutlined />,
      description: 'Confirm and launch'
    },
    {
      title: 'Knowledge Base',
      icon: <FileTextOutlined />,
      description: 'Enrich with RAG'
    }
  ];

  // Start intelligent bot creation workflow
  const startWorkflow = async (values) => {
    setIsProcessing(true);
    setReasoning('');
    setNeedsApproval(false);
    setWorkflowHistory([]);

    try {
      const response = await apiClient.post('/bots/create-workflow', {
        purpose: values.purpose,
        description: values.description,
        useCase: values.useCase
      });

      if (response.data.success) {
        setWorkflowState(response.data.workflowState);
        setCurrentStep(1);
        setReasoning(response.data.reasoning || '');
        
        // Initialize custom functions from AI suggestions
        if (response.data.customFunctions && response.data.customFunctions.length > 0) {
          setCustomFunctions(response.data.customFunctions);
          message.info(`AI suggested ${response.data.customFunctions.length} custom function(s)!`);
        } else {
          setCustomFunctions([]);
        }
        
        if (response.data.needsApproval) {
          setNeedsApproval(true);
          setApprovalQuestion(response.data.approvalQuestion || '');
        }

        // Pre-fill form with suggestions
        if (response.data.suggestions) {
          const suggestedValues = {};
          response.data.suggestions.forEach(suggestion => {
            if (suggestion.field && suggestion.value !== undefined && suggestion.value !== null) {
              // Handle array values (like enabledFunctions)
              if (Array.isArray(suggestion.value)) {
                suggestedValues[suggestion.field] = suggestion.value;
              } else {
                suggestedValues[suggestion.field] = suggestion.value;
              }
            }
          });
          form.setFieldsValue(suggestedValues);
          
          // Also update workflowState config with suggestions
          if (response.data.workflowState) {
            response.data.workflowState.config = {
              ...response.data.workflowState.config,
              ...suggestedValues
            };
            setWorkflowState(response.data.workflowState);
          }
        }

        message.success('Workflow started! AI is analyzing your requirements...');
      }
    } catch (error) {
      message.error('Failed to start workflow');
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Continue workflow to next step
  const continueWorkflow = async (stepData) => {
    setIsProcessing(true);
    setReasoning('');

    try {
      const response = await apiClient.post('/bots/continue-workflow', {
        workflowState,
        currentStep,
        stepData,
        approved: needsApproval ? true : null
      });

      if (response.data.success) {
        setWorkflowState(response.data.workflowState);
        setReasoning(response.data.reasoning || '');
        setCustomFunctions(response.data.customFunctions || []);
        setCustomFunctionsCreated(response.data.customFunctionsCreated || []);
        setCustomFunctionErrors(response.data.customFunctionErrors || []);
        
        // Add to history
        setWorkflowHistory(prev => [...prev, {
          step: currentStep,
          reasoning: response.data.reasoning,
          timestamp: new Date()
        }]);

        if (response.data.needsApproval) {
          setNeedsApproval(true);
          setApprovalQuestion(response.data.approvalQuestion || '');
          return; // Wait for approval
        }

        if (response.data.completed) {
          message.success('Bot created successfully!');
          onUpdate();
          const templateId = response.data.botId || workflowState?.config?.templateId;
          setCreatedBotId(templateId);
          setCurrentStep(5); // Go to Knowledge Base step
        } else {
          // Special handling for custom function step
          if (currentStep === 2) {
            // After function selection, go to custom functions step
            setCurrentStep(2.5);
          } else if (currentStep === 2.5) {
            // Custom functions step handled separately
            // Don't auto-advance here - user clicks button to create
            return;
          } else {
            setCurrentStep(prev => prev + 1);
          }
          message.success('Step completed! Moving to next step...');
        }
      }
    } catch (error) {
      message.error('Failed to continue workflow');
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle approval
  const handleApproval = (approved) => {
    setNeedsApproval(false);
    if (approved) {
      continueWorkflow({ approved: true });
    } else {
      // User rejected, ask for alternative
      Modal.confirm({
        title: 'Alternative Approach',
        content: 'Would you like the AI to suggest an alternative approach?',
        onOk: () => {
          continueWorkflow({ approved: false, requestAlternative: true });
        }
      });
    }
  };

  // Interrupt workflow (user can pause and modify)
  const interruptWorkflow = () => {
    Modal.confirm({
      title: 'Interrupt Workflow',
      content: 'This will pause the workflow. You can modify settings and resume later.',
      onOk: () => {
        message.info('Workflow paused. You can modify settings and resume.');
      }
    });
  };

  // Reset workflow
  const resetWorkflow = () => {
    setCurrentStep(0);
    setWorkflowState(null);
    setCreatedBotId(null);
    setReasoning('');
    setNeedsApproval(false);
    setApprovalQuestion('');
    setCustomFunctions([]);
    setCustomFunctionsCreated([]);
    setCustomFunctionErrors([]);
    setWorkflowHistory([]);
    setShowCustomFunctionForm(false);
    setEditingCustomFunction(null);
    form.resetFields();
  };

  // Handle custom function form submit
  const handleCustomFunctionSubmit = (functionData) => {
    if (editingCustomFunction !== null) {
      // Update existing
      const updated = [...customFunctions];
      updated[editingCustomFunction] = functionData;
      setCustomFunctions(updated);
      setEditingCustomFunction(null);
    } else {
      // Add new
      setCustomFunctions([...customFunctions, functionData]);
    }
    setShowCustomFunctionForm(false);
    message.success('Custom function added!');
  };

  // Remove custom function
  const removeCustomFunction = (index) => {
    const updated = customFunctions.filter((_, i) => i !== index);
    setCustomFunctions(updated);
    message.success('Custom function removed');
  };

  // Edit custom function
  const editCustomFunction = (index) => {
    setEditingCustomFunction(index);
    setShowCustomFunctionForm(true);
  };

  // Handle form submission for each step
  const handleStepSubmit = async (values) => {
    if (currentStep === 2.5) {
      // Custom functions step - include custom functions in step data
      await continueWorkflow({ customFunctions: customFunctions });
    } else {
      await continueWorkflow(values);
    }
  };

  // Create custom functions (step 3.5)
  const handleCreateCustomFunctions = async () => {
    if (customFunctions.length === 0) {
      message.info('No custom functions to create. Continuing...');
      setCurrentStep(3);
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post('/bots/continue-workflow', {
        workflowState,
        currentStep: 3.5, // Create custom functions
        stepData: { customFunctions },
        approved: null
      });

      if (response.data.success) {
        setWorkflowState(response.data.workflowState);
        setCustomFunctionsCreated(response.data.customFunctionsCreated || []);
        setCustomFunctionErrors(response.data.customFunctionErrors || []);
        
        if (response.data.customFunctionErrors && response.data.customFunctionErrors.length > 0) {
          message.warning('Some custom functions had errors. Check the details below.');
        } else {
          message.success(`Successfully created ${response.data.customFunctionsCreated?.length || 0} custom function(s)!`);
        }
        
        setCurrentStep(3); // Continue to routing
      }
    } catch (error) {
      message.error('Failed to create custom functions');
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bot-manager-container">
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ fontFamily: "'Outfit', sans-serif", margin: 0 }}>
          <RobotOutlined style={{ marginRight: 12, color: 'var(--primary-color)' }} /> 
          Intelligent Bot Creator
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 8 }}>
          Create bots with AI-powered reasoning. The system will analyze your requirements 
          and guide you through a tailored configuration process.
        </Paragraph>
      </div>

      <Steps 
        current={currentStep === 2.5 ? 3 : currentStep === 3.5 ? 3 : currentStep} 
        items={steps} 
        className="premium-steps"
        style={{ marginBottom: '48px' }}
      />

        {/* Approval Modal */}
        <Modal
          title="AI Suggestion - Approval Required"
          open={needsApproval}
          onOk={() => handleApproval(true)}
          onCancel={() => handleApproval(false)}
          okText="Approve"
          cancelText="Reject"
        >
          <Alert
            message="AI Reasoning"
            description={approvalQuestion}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          <Paragraph>
            The AI has analyzed your requirements and suggests this approach. 
            Do you want to proceed?
          </Paragraph>
        </Modal>

        {/* Reasoning Display */}
        {reasoning && (
          <Alert
            message="AI Reasoning"
            description={reasoning}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
            icon={<QuestionCircleOutlined />}
          />
        )}

        {/* Workflow History */}
        {workflowHistory.length > 0 && (
          <Card size="small" title="Workflow History" style={{ marginBottom: '16px' }}>
            {workflowHistory.map((item, idx) => (
              <div key={idx} style={{ marginBottom: '8px', fontSize: '12px' }}>
                <Tag>Step {item.step + 1}</Tag>
                <Text type="secondary">{item.reasoning}</Text>
              </div>
            ))}
          </Card>
        )}

        {/* Step 0: Initial Purpose */}
        {currentStep === 0 && (
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={startWorkflow}
            >
              <Form.Item
                name="purpose"
                label="What is the main purpose of this bot?"
                rules={[{ required: true, message: 'Please describe the bot purpose' }]}
              >
                <Input placeholder="e.g., Customer support, Travel booking, Insurance quotes" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Detailed Description"
                rules={[{ required: true, message: 'Please provide a detailed description' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Describe what the bot should do, what problems it should solve, and any specific requirements..."
                />
              </Form.Item>

              <Form.Item
                name="useCase"
                label="Primary Use Case"
              >
                <Select placeholder="Select primary use case">
                  <Option value="customer_support">Customer Support</Option>
                  <Option value="sales">Sales & Lead Generation</Option>
                  <Option value="booking">Booking & Reservations</Option>
                  <Option value="information">Information & FAQ</Option>
                  <Option value="workflow">Workflow Automation</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<PlayCircleOutlined />}
                  loading={isProcessing}
                  size="large"
                >
                  Start Intelligent Bot Creation
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Step 1: Configuration */}
        {currentStep === 1 && workflowState && (
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleStepSubmit}
              initialValues={workflowState.config || {}}
            >
              <Alert 
                message="Account & Identity"
                description="Choose the WhatsApp connection for this bot and its basic identity."
                type="info"
                style={{ marginBottom: 24 }}
              />

              <Form.Item
                name="accountId"
                label="WhatsApp Business Account (WABA)"
                rules={[{ required: true, message: 'Please select an account' }]}
                initialValue={account?._id}
              >
                <Select placeholder="Select WABA Account">
                  <Option value={account?._id}>{account?.name} ({account?.phoneDigits})</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="templateId"
                label="Bot Unique ID"
                rules={[{ required: true, message: 'Unique ID is required' }]}
                extra="A unique short identifier for system logs."
              >
                <Input placeholder="e.g., support-agent-01" />
              </Form.Item>

              <Form.Item
                name="agentName"
                label="Bot Name"
                rules={[{ required: true, message: 'Bot name is required' }]}
              >
                <Input placeholder="e.g., Customer Support Bot" />
              </Form.Item>

              <Form.Item
                name="systemPrompt"
                label="System Prompt"
                rules={[{ required: true, message: 'System prompt is required' }]}
              >
                <TextArea 
                  rows={6} 
                  placeholder="Define how the bot should behave, its personality, and guidelines..."
                />
              </Form.Item>

              <Form.Item
                name="guardRailPrompt"
                label="Guard Rail Prompt (optional)"
                extra="Boundaries the agent must never cross. Replies are checked and replaced if they violate."
              >
                <TextArea 
                  rows={3} 
                  placeholder="e.g. Never share personal data. Never give medical/legal advice. Stay on topic."
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={isProcessing}
                  >
                    Continue
                  </Button>
                  <Button onClick={interruptWorkflow} icon={<PauseCircleOutlined />}>
                    Pause
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Step 2: Functions */}
        {currentStep === 2 && workflowState && (
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleStepSubmit}
              initialValues={workflowState.functions || { enabledFunctions: [] }}
            >
              <Form.Item
                name="enabledFunctions"
                label="Select Existing Functions"
              >
                <Checkbox.Group>
                  {functions.map(func => (
                    <div key={func.name} style={{ marginBottom: '8px' }}>
                      <Checkbox value={func.name}>
                        <strong>{func.name}</strong>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {func.description || 'No description'}
                        </Text>
                      </Checkbox>
                    </div>
                  ))}
                </Checkbox.Group>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isProcessing}>
                    Continue
                  </Button>
                  <Button onClick={interruptWorkflow} icon={<PauseCircleOutlined />}>
                    Pause
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Step 2.5: Custom Functions */}
        {currentStep === 2.5 && workflowState && (
          <Card>
            <Title level={4}>Create Custom Functions</Title>
            <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
              {customFunctions.length === 0 && (
                'No custom functions suggested by AI. You can create custom functions to add API integrations, webhooks, or custom capabilities to your bot.'
              )}
              {customFunctions.length > 0 && (
                `AI suggested ${customFunctions.length} custom function(s). Review and edit them below, or create additional ones.`
              )}
            </Paragraph>

            {/* AI Suggested Custom Functions */}
            {customFunctions.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={5}>AI Suggested Custom Functions</Title>
                <List
                  dataSource={customFunctions}
                  renderItem={(func, index) => (
                    <List.Item
                      actions={[
                        <Button 
                          type="link" 
                          icon={<EditOutlined />}
                          onClick={() => editCustomFunction(index)}
                        >
                          Edit
                        </Button>,
                        <Button 
                          type="link" 
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeCustomFunction(index)}
                        >
                          Remove
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Text strong>{func.name || func.functionId}</Text>
                            <Tag color="blue">{func.httpMethod || 'POST'}</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Text type="secondary">{func.description || 'No description'}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Endpoint: {func.endpoint || 'Not set'}
                            </Text>
                            {func.reason && (
                              <>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                  AI Reasoning: {func.reason}
                                </Text>
                              </>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Custom Function Form Modal */}
            <Modal
              title={editingCustomFunction !== null ? 'Edit Custom Function' : 'Create Custom Function'}
              open={showCustomFunctionForm}
              onCancel={() => {
                setShowCustomFunctionForm(false);
                setEditingCustomFunction(null);
              }}
              footer={null}
              width={800}
            >
              <FunctionForm
                initialValues={editingCustomFunction !== null ? customFunctions[editingCustomFunction] : null}
                onSubmit={handleCustomFunctionSubmit}
                onCancel={() => {
                  setShowCustomFunctionForm(false);
                  setEditingCustomFunction(null);
                }}
              />
            </Modal>

            {/* Add Custom Function Button */}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCustomFunction(null);
                setShowCustomFunctionForm(true);
              }}
              block
              style={{ marginBottom: '24px' }}
            >
              {customFunctions.length === 0 ? 'Create Custom Function' : 'Add Another Custom Function'}
            </Button>

            {/* Custom Functions Created Status */}
            {customFunctionsCreated.length > 0 && (
              <Alert
                message="Custom Functions Created"
                description={
                  <div>
                    {customFunctionsCreated.map((func, idx) => (
                      <div key={idx} style={{ marginTop: '8px' }}>
                        <Badge 
                          status={func.status === 'created' ? 'success' : 'default'} 
                          text={`${func.functionId} - ${func.message}`}
                        />
                      </div>
                    ))}
                  </div>
                }
                type="success"
                style={{ marginBottom: '16px' }}
              />
            )}

            {customFunctionErrors.length > 0 && (
              <Alert
                message="Custom Function Errors"
                description={
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {customFunctionErrors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                }
                type="error"
                style={{ marginBottom: '16px' }}
              />
            )}

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleCreateCustomFunctions}
                  loading={isProcessing}
                >
                  Create Custom Functions & Continue
                </Button>
                <Button onClick={() => {
                  setCurrentStep(2);
                  message.info('You can skip custom functions and continue');
                }}>
                  Skip
                </Button>
                <Button onClick={interruptWorkflow} icon={<PauseCircleOutlined />}>
                  Pause
                </Button>
              </Space>
            </Form.Item>
          </Card>
        )}

        {/* Step 3: Intelligence & Routing */}
        {currentStep === 3 && workflowState && (
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleStepSubmit}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <Title level={5}><InteractionOutlined /> Handover & Collaborative Mode</Title>
                  <Paragraph type="secondary">Control how the bot works with human agents.</Paragraph>
                  
                  <Form.Item
                    name="handoverEnabled"
                    label="Enable Human Handover"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Checkbox>Allow bot to transfer conversations to humans</Checkbox>
                  </Form.Item>

                  <Form.Item
                    name="aiTakebackMinutes"
                    label="AI Takeback (Minutes)"
                    initialValue={30}
                    extra="AI resumes if human agent is silent for this long."
                  >
                    <Select>
                      <Option value={15}>15 Minutes</Option>
                      <Option value={30}>30 Minutes</Option>
                      <Option value={60}>1 Hour</Option>
                      <Option value={120}>2 Hours</Option>
                    </Select>
                  </Form.Item>
                </div>

                <div>
                  <Title level={5}><ForkOutlined /> Inbound Routing Rules</Title>
                  <Paragraph type="secondary">Decide which messages reach this bot.</Paragraph>

                  <Form.Item
                    name="routingType"
                    label="Primary Trigger"
                    initialValue="intelligent"
                  >
                    <Select placeholder="Select routing type">
                      <Option value="intelligent">Intelligent AI Detection</Option>
                      <Option value="keyword">Keyword Matching</Option>
                      <Option value="user">User Mapping (Direct)</Option>
                      <Option value="default">Default Fallback</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="routingConfig"
                    label="Routing Logic / Tags"
                  >
                    <TextArea 
                      rows={3} 
                      placeholder="e.g. Respond to messages about 'support' or 'refunds'..."
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider />

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isProcessing}>
                    Continue to Review
                  </Button>
                  <Button onClick={interruptWorkflow} icon={<PauseCircleOutlined />}>
                    Pause
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && workflowState && (
          <Card>
            <Title level={4} style={{ fontFamily: "'Outfit', sans-serif" }}>Review Bot Configuration</Title>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <Card size="small" title={<b>📝 Bot Configuration</b>} className="review-card">
                <p><strong>Template ID:</strong> {workflowState.config?.templateId}</p>
                <p><strong>Bot Name:</strong> {workflowState.config?.agentName}</p>
                <p><strong>System Prompt:</strong> <Text type="secondary">{workflowState.config?.systemPrompt?.substring(0, 100)}...</Text></p>
              </Card>

              <Card size="small" title={<b>⚙️ Functions</b>} className="review-card">
                <p><strong>Enabled:</strong> {workflowState.functions?.enabledFunctions?.join(', ') || 'None'}</p>
                <p><strong>Custom Functions:</strong> {customFunctionsCreated.length} Created</p>
              </Card>
            </div>
            <Card size="small" title="Enabled Functions" style={{ marginBottom: '16px' }}>
              <Space wrap>
                {(workflowState.functions?.enabledFunctions || []).map(func => (
                  <Tag key={func} color="blue">{func}</Tag>
                ))}
              </Space>
            </Card>

            {/* Custom Functions Summary */}
            {customFunctionsCreated.length > 0 && (
              <Card size="small" title="Custom Functions Created" style={{ marginBottom: '16px' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {customFunctionsCreated.map((func, idx) => (
                    <div key={idx}>
                      <Badge 
                        status={func.status === 'created' ? 'success' : 'default'} 
                        text={`${func.functionId} - ${func.status}`}
                      />
                    </div>
                  ))}
                </Space>
              </Card>
            )}

            {/* Routing Summary */}
            <Card size="small" title="Routing Configuration" style={{ marginBottom: '16px' }}>
              <p><strong>Type:</strong> {workflowState.routing?.type || 'Not set'}</p>
            </Card>

            {/* Full Configuration (Collapsible) */}
            <Collapse style={{ marginBottom: '16px' }}>
              <Panel header="View Full Configuration (JSON)" key="fullConfig">
                <pre style={{ background: '#f5f5f5', padding: '16px', borderRadius: '4px', maxHeight: '400px', overflow: 'auto' }}>
                  {JSON.stringify(workflowState, null, 2)}
                </pre>
              </Panel>
            </Collapse>

            <Form.Item style={{ marginTop: '16px' }}>
              <Space>
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => handleStepSubmit({ finalize: true })}
                  loading={isProcessing}
                  icon={<CheckOutlined />}
                >
                  Create Bot
                </Button>
                <Button onClick={() => setCurrentStep(0)} icon={<CloseOutlined />}>
                  Start Over
                </Button>
              </Space>
            </Form.Item>
          </Card>
        )}

        {/* Step 5: Knowledge Base (after bot created) */}
        {currentStep === 5 && createdBotId && (
          <Card>
            <Title level={4}>Knowledge Base (RAG)</Title>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Your bot was created. Optionally add documents so the bot can answer from this knowledge base.
            </Paragraph>
            <KnowledgeBaseManager templateId={createdBotId} onUpdate={onUpdate} />
            <Form.Item style={{ marginTop: 24 }}>
              <Button type="primary" size="large" onClick={resetWorkflow} icon={<CheckOutlined />}>
                Done
              </Button>
            </Form.Item>
          </Card>
        )}

        {isProcessing && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: '16px' }}>
              <Text>AI is analyzing and reasoning...</Text>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  export default BotManager;

